/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { deleteData, fetchData, postData, patchData } from '@jda/lui-portal-utilities';
import { getAuthToken } from '../auth/authProvider';
import { IClient, IClientRequest, IRealms } from '../models/Machines/ClientContracts';
import { convertObjectToQueryString, createRequestObject, getAPIMUrl } from '../utility/commonUtility';

const { CLIENT_SERVICE_V1_URL, ENTITLEMENT_SERVICE, USER_MACHINE_SERVICE, MODULE_SERVICE } = window['env'];

export interface getAllClientsProps {
  searchText?: string;
  type?: string;
  referenceType?: string;
  source?: string;
  pageNo?: number;
  limit?: number;
}

export const getAllClients = async (queryParams?: getAllClientsProps) => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines?pageNumber=1&pageSize=10000`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<IClient[]>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getClientById = async (id: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${id}`;

  return fetchData<IClient>(createRequestObject(apiURL), getAuthToken);
};

export const createNewClient = async (data: IClientRequest) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines`;

  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const actionOnClient = async (id: string, action: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  return postData<any>(createRequestObject(`${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${id}`), {
    action
  })
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const deleteClient = async (id: number | string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${id}`;
  return deleteData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const updateClient = async (id: number | string, data: { displayName: string }) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${id}`;
  return patchData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const createNewClientSecret = async (machineId: string, data?: { expiryDays?: number }) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${machineId}/secret`;
  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const deleteClientSecret = async (machineId: string, keyId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${machineId}/secret`;

  return deleteData(createRequestObject(apiURL), { keyId: keyId })
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const rotateClientSecret = async (machineId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${machineId}/secret`;

  return postData(createRequestObject(apiURL), {})
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const getAllowedRealms = async (clientId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/machines/${clientId}/allowedRealms`;

  return fetchData<IRealms>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getRealmEntitlements = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = '';
  // let queryData = '';
  apiURL = `${BASE_APIM_URL}${ENTITLEMENT_SERVICE}/realmEntitlements`;
  // if (!queryParams?.type || queryParams?.type === 'external') {
  //   apiURL = `${BASE_APIM_URL}${ENTITLEMENT_SERVICE}/realmEntitlements`;
  // } else {
  //   apiURL = `${BASE_APIM_URL}${MODULE_SERVICE}/modules`;
  // }
  // if (queryParams && typeof queryParams === 'object') {
  //   queryData = convertObjectToQueryString(queryParams);
  //   apiURL = `${apiURL}?${queryData}`;
  // }
  return fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const getModuleApis = async (
  module: string,
  queryParams?: { page?: number; pageSize?: number; apiType?: string }
) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let queryData = '';
  const lowerCaseModule = module.toLowerCase();
  let apiURL = `${BASE_APIM_URL}${MODULE_SERVICE}/${lowerCaseModule}/apiversions`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const getMachineSubscriptions = async (
  machineId: string,
  queryParams?: { 'page-number'?: number; 'page-size'?: number }
) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let queryData = '';
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${machineId}/subscriptions`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const addMachineSubscriptions = async (
  machineId: string,
  apis: { name: string; version: string; moduleKey: string }[]
) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${machineId}/subscriptions`;

  return postData<any>(createRequestObject(apiURL), { apis }, getAuthToken);
};

export const removeMachineSubscriptions = async (machineId: string, apis: { name: string; version: string }[]) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/${machineId}/subscriptions`;

  return deleteData(createRequestObject(apiURL), { apis }, getAuthToken);
};

export const getTopicSubscriptions = async (
  module: { moduleKey: string } | null | '',
  machineId: string | undefined,
  queryParams?: { pageNumber?: number; pageSize?: number }
) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let data = module ? { moduleKeys: [module?.moduleKey] } : {};
  let queryData = '';
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/event/subscription/${machineId}/topics`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return postData<any>(createRequestObject(apiURL), data, getAuthToken);
};

export const getModuleTopics = async (
  module: { moduleKey: string } | null | '',
  queryParams?: { pageNumber?: number; pageSize?: number }
) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let data = module ? { moduleKeys: [module?.moduleKey] } : {};
  let queryData = '';
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/event/topicsByRealm`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }
  return postData<any>(createRequestObject(apiURL), data, getAuthToken);
  
};
export const addMachineTopicSubscriptions = async (machineId: string | undefined, subscriptionTopics: { topicName: string; topicAccess: string }[]) => { 
const data = {
    subscriptionTopics
  };
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/event/${machineId}/topics`;

  return postData(createRequestObject(apiURL), data, getAuthToken);
}

 
export const updateMachineTopicSubscriptions = async (
  machineId: string | undefined,
  subscriptionName: string,
  subscriptionTopics: { topicName: string; topicAccess: string }[]
) => {
  const data = {
    operation: 'add',
    subscriptionTopics,
    subscriptionReason: `add`
  };
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/event/${machineId}/subscription/${subscriptionName}`;

  return patchData(createRequestObject(apiURL), data, getAuthToken);
};
export const removeMachineTopicSubscriptions = async (
  machineId: string | undefined,
  subscriptionName: string,
  subscriptionTopics: { topicName: string; topicAccess: string }[]
) => {
  const data = {
    operation: 'remove',
    subscriptionTopics
  };
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/event/${machineId}/subscription/${subscriptionName}`;

  return patchData(createRequestObject(apiURL), data, getAuthToken);
};
export const deleteMachineTopicSubscription = async (subscriptionName: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/machines/event/${subscriptionName}`;

  return deleteData(createRequestObject(apiURL), getAuthToken);
};
